import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Period } from '@/lib/timePeriod';
import type {
  OffenderEntity,
  TopOffendersResponse,
} from '@/types/offenders';

export interface UseTopOffendersOptions {
  entity: OffenderEntity;
  period: Period;
  /** Default 10 — matches the visual size of the Top Offenders panel. */
  limit?: number;
  /** Default false — Watch alerts are DQ noise; investigators rarely want them. */
  includeWatch?: boolean;
}

/**
 * Fetches the top N offenders (by alert count, then risk score sum, then
 * recency) for the selected entity + period. Backs the Top Offenders
 * panel on the Fraud Hub; wire shape is generic enough that future
 * Drivers / Cards / Stations hubs can reuse this hook by passing a
 * different `entity`.
 *
 * Cache key includes every input so toggling entity / period / limit /
 * watch each flip to a fresh query and don't cross-contaminate.
 */
export function useTopOffenders({
  entity,
  period,
  limit = 10,
  includeWatch = false,
}: UseTopOffendersOptions): UseQueryResult<TopOffendersResponse, Error> {
  return useQuery<TopOffendersResponse, Error>({
    queryKey: ['offenders', entity, period, limit, includeWatch],
    queryFn: async () => {
      const { data } = await api.get<TopOffendersResponse>('/offenders', {
        params: {
          entity,
          period,
          limit,
          include_watch: includeWatch,
        },
      });
      return data;
    },
    staleTime: 30_000,
  });
}
